import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "d-flex align-center mr-4 mt-2",
  style: {"gap":"10px"}
}

import { onMounted, computed, ref, watch } from 'vue';
import { usePreferencesStore } from '@/store/preferences';
import { useClientsStore } from '@/store/clients';
import { useModuleLayoutFetcher } from '@/composables/useModuleLayoutFetcher';
import { HEADERS, DEFAULT_HEADER_ORDER } from '@/components/modules/clients/clients.config';
import { useSendFileDialogStore } from '@/store/sendFileDialog';
import { useSendersStore } from '@/store/senders';
import { CARD_FIELDS, DEFAULT_CARD_ORDER } from '@/components/modules/sales/sales.config';
import { useDisplay } from 'vuetify';
import { remoteOptionsRegistry } from '@/lib/remoteOptions';
import { useAlertStore } from '@/store/alertStore';
import { useLayoutField } from '@/composables/useLayoutField';

import Table from '@/components/views/TableView.vue';
import MobileCardsView from '@/components/views/MobileCardsView.vue';
import Filter from '@/components/drawer/Filters.vue';
import ViewsContainer from '@/components/views/ViewsContainer.vue';

const moduleName = 'Clients';

export default /*@__PURE__*/_defineComponent({
  __name: 'Clients',
  setup(__props) {

const { mobile } = useDisplay();
const isMobile = computed(() => mobile.value);

const preferencesStore = usePreferencesStore();
const clientsStore = useClientsStore();
const alertStore = useAlertStore();
const sendFileDialogStore = useSendFileDialogStore();
const sendersStore = useSendersStore();

const getHostnameSafe = (): string => {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname;
  }
  return process.env.VITE_PUBLIC_HOST ?? process.env.SSR_HOST ?? '';
};
const IS_DOMINIOMEDIOS = getHostnameSafe() === 'crm.dominiomedios.com';

const showClientTypeTabs = !IS_DOMINIOMEDIOS;
const clientTypeTabs = ref<Array<{ label: string; value: any }>>(
  showClientTypeTabs ? [{ label: 'Todos', value: 'all' }] : []
);
const mergeTagTabs = ref<Array<{ label: string; value: string }>>([
  { label: 'Agencia', value: 'Agencia' },
  { label: 'Cuenta', value: 'Cuenta' },
]);
const mergeTabValues = ['Agencia', 'Cuenta'] as const;
const currentView = useLayoutField(moduleName, 'currentView');
const filters = useLayoutField(moduleName, 'filters');
const mergeSelectedTag = useLayoutField(moduleName, 'mergeSelectedTag');

const {
  create,
  update,
  remove,
  exportCsv,
  reload
} = useModuleLayoutFetcher(
  moduleName,
  (params) => clientsStore.fetchClients(params),
  {
    create: clientsStore.createClient,
    update: (data) => clientsStore.updateClient(data.id, data),
    remove: clientsStore.deleteClient,
    exportCsv: clientsStore.exportCSV
  }
);

onMounted(async () => {
  preferencesStore.currentModule = moduleName;
  // Registra los headers y defaults para ESTE módulo:
  preferencesStore.setAllModuleHeaders(moduleName, HEADERS);
  preferencesStore.setDefaultModuleHeaders(
    moduleName,
    DEFAULT_HEADER_ORDER
      .map(value => HEADERS.find(header => header.value === value))
      .filter(Boolean) as typeof HEADERS
  );

  if (showClientTypeTabs && remoteOptionsRegistry.getClientTypes) {
    const types = await remoteOptionsRegistry.getClientTypes('');
    clientTypeTabs.value = [
      { label: 'Todos', value: 'all' },
      ...types.map((t: any) => ({ label: t?.name ?? t?.label ?? 'Sin nombre', value: t?.id ?? t }))
    ];
  }

  // Reset transient merge state on mount to avoid persisted localStorage lockups.
  mergeMode.value = false;
  mergeSelectedTag.value = 'Agencia';
  selectedClientIds.value = [];
  showMergeDialog.value = false;
  primaryClientId.value = null;

  // Normal mode defaults after hard refresh:
  // - clear merge-only filter
  // - keep selected type if exists, otherwise default to "Todos"
  const next = { ...(filters.value || {}) } as Record<string, any>;
  delete next.tag;
  if (next.client_type_id === 'all') delete next.client_type_id;
  filters.value = next;
});

const applyMergeTypeFilter = (base: Record<string, any>, tag: string) => {
  const next = { ...base };
  // Keep UI tab state stable for Toolbar selection.
  next.tag = tag;
  delete next.client_type_id;
  return next;
};

const sendMediaKit = (item:any) => {
  sendFileDialogStore.openForm({
    formTitle: 'Enviar Media Kit',
    main_email: item?.attributes?.email,
    client_id: item?.id,
    onSendEmail: sendersStore.sendEmailWithMediaKit,
    onSendWhatsapp: sendersStore.sendWhatsAppWithMediaKit,
  });
};

const rowActionHandlers = {
  sendMediaKit
};

const data = computed(() => clientsStore.clients);

const mergeMode = useLayoutField(moduleName, 'mergeMode');
const selectedClientIds = ref<Array<number | string>>([]);
const showMergeDialog = ref(false);
const primaryClientId = ref<number | null>(null);
const isMerging = ref(false);

const selectedClients = computed(() => {
  const selectedSet = new Set(selectedClientIds.value.map(Number));
  return clientsStore.clients.filter((client: any) => selectedSet.has(Number(client.id)));
});

const selectedClientOptions = computed(() =>
  selectedClients.value.map((client: any) => ({
    id: Number(client.id),
    label: `${client.attributes?.commercial_name || client.attributes?.legal_name || 'Cliente'} (#${client.id})`,
    clientTypeId: client.attributes?.client_type?.id ?? null,
    clientTypeTag: client.attributes?.client_type?.tag ?? null,
  }))
);

const typeValidationError = computed(() => {
  if (selectedClientOptions.value.length < 2) return '';
  const typeIds = new Set(selectedClientOptions.value.map((c: any) => c.clientTypeId));
  const typeTags = new Set(selectedClientOptions.value.map((c: any) => c.clientTypeTag).filter(Boolean));

  if (typeIds.size > 1 || typeTags.size > 1) {
    return 'Solo puedes fusionar clientes del mismo tipo: Agencia con Agencia o Cuenta con Cuenta.';
  }
  return '';
});

const canConfirmMerge = computed(() =>
  selectedClientIds.value.length >= 2 &&
  !!primaryClientId.value &&
  !typeValidationError.value
);

const enableMergeMode = () => {
  mergeMode.value = true;
  selectedClientIds.value = [];
  currentView.value = 'table';
  const next = { ...(filters.value || {}) };
  mergeSelectedTag.value = mergeTabValues.includes(mergeSelectedTag.value as any)
    ? mergeSelectedTag.value
    : 'Agencia';
  filters.value = applyMergeTypeFilter(next as Record<string, any>, mergeSelectedTag.value as string);
};

const cancelMergeMode = () => {
  mergeMode.value = false;
  selectedClientIds.value = [];
  showMergeDialog.value = false;
  primaryClientId.value = null;
  const next = { ...(filters.value || {}) };
  delete next.tag;
  delete (next as Record<string, any>).client_type_id;
  filters.value = next;
  mergeSelectedTag.value = 'Agencia';
};

watch(mergeMode, (enabled) => {
  if (enabled) {
    const current = { ...(filters.value || {}) } as Record<string, any>;
    const tag = mergeTabValues.includes(mergeSelectedTag.value as any)
      ? (mergeSelectedTag.value as string)
      : 'Agencia';
    filters.value = applyMergeTypeFilter(current, tag);
    return;
  }

  if (!enabled) {
    const next = { ...(filters.value || {}) } as Record<string, any>;
    delete next.tag;
    delete next.client_type_id;
    filters.value = next;
  }
});

watch(
  () => filters.value?.tag,
  (tagVal) => {
    if (!mergeMode.value) return;
    const next = { ...(filters.value || {}) } as Record<string, any>;
    const incomingTag = tagVal as string | undefined;
    if (incomingTag && mergeTabValues.includes(incomingTag as any) && mergeSelectedTag.value !== incomingTag) {
      mergeSelectedTag.value = incomingTag;
    }
    const selected = mergeSelectedTag.value && mergeTabValues.includes(mergeSelectedTag.value as any)
      ? (mergeSelectedTag.value as string)
      : 'Agencia';
    const merged = applyMergeTypeFilter(next, selected);
    if (JSON.stringify(merged) !== JSON.stringify(next)) {
      filters.value = merged;
    }
  }
);

const onSelectedClientsChange = (items: Array<number | string>) => {
  selectedClientIds.value = (items || []).map((item: any) =>
    typeof item === 'object' && item !== null ? Number(item.id) : Number(item)
  );
};

const openMergeDialog = () => {
  if (selectedClientIds.value.length < 2) return;
  primaryClientId.value = Number(selectedClientIds.value[0]);
  showMergeDialog.value = true;
};

const confirmMerge = async () => {
  if (!canConfirmMerge.value || !primaryClientId.value) return;
  try {
    isMerging.value = true;
    await clientsStore.mergeClients({
      client_ids: selectedClientIds.value.map(Number),
      primary_client_id: Number(primaryClientId.value),
    });

    showMergeDialog.value = false;
    cancelMergeMode();
    await reload();
    alertStore.showAlert('Los clientes seleccionados fueron fusionados correctamente.', 'success');
  } catch (error: any) {
    alertStore.showAlert(
      error?.response?.data?.message || 'Ocurrió un error al fusionar clientes.',
      'error'
    );
  } finally {
    isMerging.value = false;
  }
};

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, null, {
    default: _withCtx(() => [
      (_openBlock(), _createBlock(ViewsContainer, {
        moduleName: moduleName,
        listViewComponent: Table,
        filterComponent: Filter,
        data: data.value,
        config: '@/components/modules/clients/clients.config',
        onCreate: _unref(create),
        onUpdate: _unref(update),
        onDelete: _unref(remove),
        exportCsv: _unref(exportCsv),
        rowActionHandlers: rowActionHandlers,
        show_toolbar: true,
        show_pagination: true,
        title: {singular:'Cliente', plural:'Clientes'},
        key: moduleName,
        tabs: _unref(mergeMode) ? mergeTagTabs.value : clientTypeTabs.value,
        tabFilterKey: _unref(mergeMode) ? 'tag' : 'client_type_id',
        showTabs: showClientTypeTabs || _unref(mergeMode),
        selectable: _unref(mergeMode),
        selectedItems: selectedClientIds.value,
        "onUpdate:selectedItems": onSelectedClientsChange
      }, {
        "before-create": _withCtx(() => [
          (_ctx.$can('clients.merge'))
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (!_unref(mergeMode))
                  ? (_openBlock(), _createBlock(_component_v_btn, {
                      key: 0,
                      color: "primary",
                      variant: "outlined",
                      onClick: enableMergeMode
                    }, {
                      default: _withCtx(() => _cache[3] || (_cache[3] = [
                        _createTextVNode(" Fusionar ")
                      ])),
                      _: 1,
                      __: [3]
                    }))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createVNode(_component_v_btn, {
                        color: "primary",
                        disabled: selectedClientIds.value.length < 2,
                        onClick: openMergeDialog
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(" Fusionar " + _toDisplayString(selectedClientIds.value.length) + " clientes ", 1)
                        ]),
                        _: 1
                      }, 8, ["disabled"]),
                      _createVNode(_component_v_btn, {
                        variant: "text",
                        onClick: cancelMergeMode
                      }, {
                        default: _withCtx(() => _cache[4] || (_cache[4] = [
                          _createTextVNode(" Cancelar ")
                        ])),
                        _: 1,
                        __: [4]
                      })
                    ], 64))
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["data", "onCreate", "onUpdate", "onDelete", "exportCsv", "tabs", "tabFilterKey", "showTabs", "selectable", "selectedItems"])),
      _createVNode(_component_v_dialog, {
        modelValue: showMergeDialog.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showMergeDialog).value = $event)),
        "max-width": "640"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "text-h6" }, {
                default: _withCtx(() => _cache[5] || (_cache[5] = [
                  _createTextVNode("Confirmar Fusión de Clientes")
                ])),
                _: 1,
                __: [5]
              }),
              _createVNode(_component_v_card_text, null, {
                default: _withCtx(() => [
                  _cache[6] || (_cache[6] = _createElementVNode("p", { class: "mb-3" }, " Selecciona el cliente principal. Los demás clientes seleccionados serán fusionados en él. ", -1)),
                  (typeValidationError.value)
                    ? (_openBlock(), _createBlock(_component_v_alert, {
                        key: 0,
                        type: "warning",
                        variant: "tonal",
                        class: "mb-4"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(typeValidationError.value), 1)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  _createVNode(_component_v_select, {
                    modelValue: primaryClientId.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((primaryClientId).value = $event)),
                    items: selectedClientOptions.value,
                    "item-title": "label",
                    "item-value": "id",
                    label: "Cliente principal",
                    disabled: isMerging.value
                  }, null, 8, ["modelValue", "items", "disabled"])
                ]),
                _: 1,
                __: [6]
              }),
              _createVNode(_component_v_card_actions, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    variant: "text",
                    disabled: isMerging.value,
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (showMergeDialog.value = false))
                  }, {
                    default: _withCtx(() => _cache[7] || (_cache[7] = [
                      _createTextVNode("Cancelar")
                    ])),
                    _: 1,
                    __: [7]
                  }, 8, ["disabled"]),
                  _createVNode(_component_v_btn, {
                    color: "primary",
                    loading: isMerging.value,
                    disabled: !canConfirmMerge.value,
                    onClick: confirmMerge
                  }, {
                    default: _withCtx(() => _cache[8] || (_cache[8] = [
                      _createTextVNode(" Confirmar ")
                    ])),
                    _: 1,
                    __: [8]
                  }, 8, ["loading", "disabled"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _: 1
  }))
}
}

})